import {
  type AdminV2EntryAction,
  adminV2EntryActionSchema,
} from '@/admin-v2/entry-actions/entry-action';
import { adminV2ExactIdentitySchema } from '@/admin-v2/foundation/admin-v2-identity';
import { adminV2LocalizedTextSchema } from '@/admin-v2/translation/translation-contract';
import { z } from 'zod';

export const adminV2WorkspaceActionSlotIds = [
  'result.feedback.positive',
  'result.feedback.negative',
] as const;

export const adminV2WorkspaceActionSlotIdSchema = z.enum(
  adminV2WorkspaceActionSlotIds
);

export type AdminV2WorkspaceActionSlotId = z.infer<
  typeof adminV2WorkspaceActionSlotIdSchema
>;

export type AdminV2WorkspaceActionSlotDefinition = {
  id: AdminV2WorkspaceActionSlotId;
  label: string;
  location: 'result_feedback';
  baseBehavior: 'record_positive_feedback' | 'record_negative_feedback';
  availableContext: Array<'currentPrimaryInput' | 'currentPrimaryResult'>;
};

const slotDefinitions = [
  {
    id: 'result.feedback.positive',
    label: 'Result feedback: positive',
    location: 'result_feedback',
    baseBehavior: 'record_positive_feedback',
    availableContext: ['currentPrimaryInput', 'currentPrimaryResult'],
  },
  {
    id: 'result.feedback.negative',
    label: 'Result feedback: negative',
    location: 'result_feedback',
    baseBehavior: 'record_negative_feedback',
    availableContext: ['currentPrimaryInput', 'currentPrimaryResult'],
  },
] as const satisfies readonly AdminV2WorkspaceActionSlotDefinition[];

export const adminV2WorkspaceActionSlotRegistry = new Map(
  slotDefinitions.map((definition) => [definition.id, definition])
);

export function listAdminV2WorkspaceActionSlots() {
  return slotDefinitions.map((definition) => ({
    ...definition,
    availableContext: [...definition.availableContext],
  }));
}

export function getAdminV2WorkspaceActionSlot(
  slotId: AdminV2WorkspaceActionSlotId
) {
  return adminV2WorkspaceActionSlotRegistry.get(slotId) ?? null;
}

export const adminV2WorkspaceActionConfirmationSchema = z
  .object({
    title: adminV2LocalizedTextSchema,
    description: adminV2LocalizedTextSchema,
    confirmLabel: adminV2LocalizedTextSchema,
    cancelLabel: adminV2LocalizedTextSchema,
  })
  .strict()
  .superRefine((confirmation, context) => {
    const allowed = new Set(['currentToolName', 'targetToolName']);
    for (const key of [
      'title',
      'description',
      'confirmLabel',
      'cancelLabel',
    ] as const) {
      const sourceTokens = listTemplateTokens(confirmation[key].en);
      for (const token of sourceTokens) {
        if (!allowed.has(token)) {
          context.addIssue({
            code: 'custom',
            message: `Workspace action confirmation uses unknown placeholder {${token}}.`,
            path: [key, 'en'],
          });
        }
      }
      for (const [locale, text] of Object.entries(confirmation[key])) {
        const tokens = listTemplateTokens(text);
        if (
          tokens.length !== sourceTokens.length ||
          tokens.some((token, index) => token !== sourceTokens[index])
        ) {
          context.addIssue({
            code: 'custom',
            message:
              'Workspace action confirmation translations must preserve source placeholders.',
            path: [key, locale],
          });
        }
      }
    }
  });

function listTemplateTokens(text: string) {
  return Array.from(text.matchAll(/\{([A-Za-z][A-Za-z0-9]*)\}/g))
    .map((match) => match[1] ?? '')
    .filter(Boolean)
    .sort();
}

export type AdminV2WorkspaceActionConfirmation = z.infer<
  typeof adminV2WorkspaceActionConfirmationSchema
>;

const actionBindingConfirmationShape = {
  confirmation: adminV2WorkspaceActionConfirmationSchema.optional(),
};

export const adminV2WorkspaceActionBindingSchema = z.discriminatedUnion(
  'mode',
  [
    z.object({ mode: z.literal('none') }).strict(),
    z
      .object({
        mode: z.literal('existingAction'),
        actionId: adminV2ExactIdentitySchema,
        ...actionBindingConfirmationShape,
      })
      .strict(),
    z
      .object({
        mode: z.literal('custom'),
        action: adminV2EntryActionSchema,
        ...actionBindingConfirmationShape,
      })
      .strict(),
  ]
);

export const adminV2WorkspaceActionSlotBindingSchema = z
  .object({
    slotId: adminV2WorkspaceActionSlotIdSchema,
    binding: adminV2WorkspaceActionBindingSchema,
  })
  .strict();

export type AdminV2WorkspaceActionBinding = z.infer<
  typeof adminV2WorkspaceActionBindingSchema
>;

export type AdminV2WorkspaceActionSlotBinding = z.infer<
  typeof adminV2WorkspaceActionSlotBindingSchema
>;

export type AdminV2ResolvedWorkspaceActionBinding = {
  slotId: AdminV2WorkspaceActionSlotId;
  source: 'existingAction' | 'custom';
  actionId?: string;
  action: AdminV2EntryAction;
  confirmation?: z.infer<typeof adminV2WorkspaceActionConfirmationSchema>;
};

export const adminV2ResolvedWorkspaceActionBindingSchema = z
  .object({
    slotId: adminV2WorkspaceActionSlotIdSchema,
    source: z.enum(['existingAction', 'custom']),
    actionId: adminV2ExactIdentitySchema.optional(),
    action: adminV2EntryActionSchema,
    confirmation: adminV2WorkspaceActionConfirmationSchema.optional(),
  })
  .strict();

export function assertAdminV2WorkspaceActionBindings(
  bindings: AdminV2WorkspaceActionSlotBinding[],
  context: z.RefinementCtx,
  path: Array<string | number>
) {
  const seen = new Set<AdminV2WorkspaceActionSlotId>();
  bindings.forEach((item, index) => {
    if (seen.has(item.slotId)) {
      context.addIssue({
        code: 'custom',
        message: `Workspace action slot ${item.slotId} may only be configured once.`,
        path: [...path, index, 'slotId'],
      });
    }
    seen.add(item.slotId);
  });
}

export function listAdminV2WorkspaceEntryActionReferences(
  bindings: AdminV2WorkspaceActionSlotBinding[] | undefined
) {
  return Array.from(
    new Set(
      (bindings ?? []).flatMap((item) =>
        item.binding.mode === 'existingAction' ? [item.binding.actionId] : []
      )
    )
  );
}

export function listAdminV2WorkspaceTargetToolReferences(
  bindings:
    | Array<{
        binding: {
          mode: 'none' | 'existingAction' | 'custom';
          action?: AdminV2EntryAction;
        };
      }>
    | undefined
) {
  return Array.from(
    new Set(
      (bindings ?? []).flatMap((item) => {
        const action =
          item.binding.mode === 'custom' ? item.binding.action : undefined;
        return action?.type === 'switch_to_workspace' && action.targetToolId
          ? [action.targetToolId]
          : [];
      })
    )
  );
}

export function listAdminV2ResolvedWorkspaceTargetToolReferences(
  bindings: AdminV2ResolvedWorkspaceActionBinding[] | undefined
) {
  return Array.from(
    new Set(
      (bindings ?? []).flatMap((item) =>
        item.action.type === 'switch_to_workspace' && item.action.targetToolId
          ? [item.action.targetToolId]
          : []
      )
    )
  );
}
